import { useEffect, useState } from "react";
import {
  fetchAdminCities,
  createAdminCity,
  updateAdminCity,
  deleteAdminCity
} from "../../services/adminService";

const EMPTY_FORM = { name: "", state: "", lat: "", lng: "", active: true };

function Cities() {
  const [list,      setList]      = useState([]);
  const [adding,    setAdding]    = useState(false);
  const [newCity,   setNewCity]   = useState(EMPTY_FORM);
  const [addError,  setAddError]  = useState("");
  const [editing,   setEditing]   = useState(null);
  const [editError, setEditError] = useState("");
  const [deleteId,  setDeleteId]  = useState(null);

  // Unique state names for datalist suggestions
  const stateOptions = [...new Set(list.map((c) => c.state))].sort();

  const loadData = async () => {
    try {
      const res = await fetchAdminCities();
      setList(res.data || []);
    } catch (err) {
      console.error("Error loading cities", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const validateForm = (form) => {
    if (!form.name.trim())  return "City / district name is required";
    if (!form.state.trim()) return "State is required";
    if (form.lat === "" || isNaN(Number(form.lat))) return "Valid latitude is required";
    if (form.lng === "" || isNaN(Number(form.lng))) return "Valid longitude is required";
    return null;
  };

  // Open edit modal — convert numbers to strings for controlled inputs
  const openEdit = (city) => {
    setEditing({ ...city, lat: String(city.lat), lng: String(city.lng) });
    setEditError("");
  };

  // ─── CRUD ─────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    const err = validateForm(newCity);
    if (err) { setAddError(err); return; }
    try {
      await createAdminCity({
        name:   newCity.name.trim(),
        state:  newCity.state.trim(),
        lat:    Number(newCity.lat),
        lng:    Number(newCity.lng),
        active: newCity.active
      });
      setNewCity(EMPTY_FORM);
      setAdding(false);
      setAddError("");
      loadData();
    } catch (err) {
      setAddError(err?.response?.data?.msg || "Error adding city");
    }
  };

  const handleUpdate = async () => {
    const err = validateForm(editing);
    if (err) { setEditError(err); return; }
    try {
      await updateAdminCity(editing._id, {
        name:   editing.name.trim(),
        state:  editing.state.trim(),
        lat:    Number(editing.lat),
        lng:    Number(editing.lng),
        active: editing.active
      });
      setEditing(null);
      setEditError("");
      loadData();
    } catch (err) {
      setEditError(err?.response?.data?.msg || "Error updating city");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteAdminCity(deleteId);
    setDeleteId(null);
    loadData();
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h2>Cities / Districts</h2>
        <button
          className="btn save"
          onClick={() => { setAdding(true); setAddError(""); setNewCity(EMPTY_FORM); }}
        >
          + Add City
        </button>
      </div>

      {list.length === 0 ? (
        <p>No cities found</p>
      ) : (
        <>
          {/* ── DESKTOP TABLE (hidden on mobile via CSS) ── */}
          <div className="admin-table-wrap">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>State</th>
                  <th>Lat</th>
                  <th>Lng</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((city) => (
                  <tr key={city._id}>
                    <td>{city.name}</td>
                    <td>{city.state}</td>
                    <td>{Number(city.lat).toFixed(4)}</td>
                    <td>{Number(city.lng).toFixed(4)}</td>
                    <td>
                      <span className={city.active ? "status-active" : "status-inactive"}>
                        {city.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-actions" style={{ marginTop: 0, paddingTop: 0, justifyContent: "flex-start" }}>
                        <button className="edit"   onClick={() => openEdit(city)}>Edit</button>
                        <button className="delete" onClick={() => setDeleteId(city._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── MOBILE CARDS (hidden on desktop via CSS) ── */}
          <div className="city-card-list">
            {list.map((city) => (
              <div key={city._id} className="admin-card">
                <h3 className="business-title">{city.name}</h3>
                <p><b>State:</b> {city.state}</p>
                <p><b>Lat:</b> {Number(city.lat).toFixed(4)}</p>
                <p><b>Lng:</b> {Number(city.lng).toFixed(4)}</p>
                <p>
                  <b>Status:</b>{" "}
                  <span className={city.active ? "status-active" : "status-inactive"}>
                    {city.active ? "Active" : "Inactive"}
                  </span>
                </p>
                <div className="admin-actions">
                  <button className="edit"   onClick={() => openEdit(city)}>Edit</button>
                  <button className="delete" onClick={() => setDeleteId(city._id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Datalist for state suggestions — reused by both add and edit modals */}
      <datalist id="state-suggestions">
        {stateOptions.map((s) => <option key={s} value={s} />)}
      </datalist>

      {/* ── ADD MODAL ─────────────────────────────────────────────────── */}
      {adding && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Add City / District</h3>

            <input
              type="text"
              placeholder="City / District name"
              value={newCity.name}
              onChange={(e) => { setNewCity({ ...newCity, name: e.target.value }); setAddError(""); }}
              className="modal-input"
            />

            <input
              type="text"
              placeholder="State (e.g. Tamil Nadu)"
              list="state-suggestions"
              value={newCity.state}
              onChange={(e) => { setNewCity({ ...newCity, state: e.target.value }); setAddError(""); }}
              className="modal-input"
            />

            <input
              type="number"
              step="any"
              placeholder="Latitude (e.g. 13.0827)"
              value={newCity.lat}
              onChange={(e) => { setNewCity({ ...newCity, lat: e.target.value }); setAddError(""); }}
              className="modal-input"
            />

            <input
              type="number"
              step="any"
              placeholder="Longitude (e.g. 80.2707)"
              value={newCity.lng}
              onChange={(e) => { setNewCity({ ...newCity, lng: e.target.value }); setAddError(""); }}
              className="modal-input"
            />

            <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "10px 0" }}>
              <input
                type="checkbox"
                id="addActive"
                checked={newCity.active}
                onChange={(e) => setNewCity({ ...newCity, active: e.target.checked })}
              />
              <label htmlFor="addActive">Active (visible in city dropdown)</label>
            </div>

            {addError && <p className="modal-error">{addError}</p>}

            <div className="modal-actions">
              <button
                className="btn cancel"
                onClick={() => { setAdding(false); setNewCity(EMPTY_FORM); setAddError(""); }}
              >
                Cancel
              </button>
              <button className="btn save" onClick={handleAdd}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ────────────────────────────────────────────────── */}
      {editing && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Edit City / District</h3>

            <input
              type="text"
              placeholder="City / District name"
              value={editing.name}
              onChange={(e) => { setEditing({ ...editing, name: e.target.value }); setEditError(""); }}
              className="modal-input"
            />

            <input
              type="text"
              placeholder="State"
              list="state-suggestions"
              value={editing.state}
              onChange={(e) => { setEditing({ ...editing, state: e.target.value }); setEditError(""); }}
              className="modal-input"
            />

            <input
              type="number"
              step="any"
              placeholder="Latitude"
              value={editing.lat}
              onChange={(e) => { setEditing({ ...editing, lat: e.target.value }); setEditError(""); }}
              className="modal-input"
            />

            <input
              type="number"
              step="any"
              placeholder="Longitude"
              value={editing.lng}
              onChange={(e) => { setEditing({ ...editing, lng: e.target.value }); setEditError(""); }}
              className="modal-input"
            />

            <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "10px 0" }}>
              <input
                type="checkbox"
                id="editActive"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              />
              <label htmlFor="editActive">Active (visible in city dropdown)</label>
            </div>

            {editError && <p className="modal-error">{editError}</p>}

            <div className="modal-actions">
              <button className="btn cancel" onClick={() => { setEditing(null); setEditError(""); }}>Cancel</button>
              <button className="btn save"   onClick={handleUpdate}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ──────────────────────────────────────── */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal-box small">
            <h3>Delete City?</h3>
            <p>Existing businesses in this city will still appear in search results.</p>

            <div className="modal-actions">
              <button className="btn cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn delete" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cities;
